import { type BaseProductElementProps } from "~/domains/product/types/BaseProductElementProps";
import { useProductContext } from "~/domains/product/ProductContext";
import { styled } from "~/common/mui";
import type { PdpLinkProps } from "~/domains/product/components/PdpLink";
import { PrimaryImageV2 } from "~/domains/product/components/PrimaryImageV2";
import { useProductDetailsUrl } from "~/features/PackageRestriction/hooks/useProductDetailsUrl";

const SIZES: Record<Sizes, { imgSize: number; gapSize: number; logoWidth: number }> = {
  tiny: { imgSize: 64, gapSize: 16, logoWidth: 32 },
  small: { imgSize: 120, gapSize: 8, logoWidth: 32 },
  huge: { imgSize: 272, gapSize: 32, logoWidth: 66 },
};

type Sizes = "tiny" | "small" | "huge";
interface Props extends BaseProductElementProps {
  size?: Sizes;
  clickable?: boolean;
  linkProps?: JSX.IntrinsicElements["a"];
}

export const PrimaryImageWithLogos = (props: Props) => {
  const { size = "small", clickable = true, linkProps } = props;
  const { imgSize, gapSize, logoWidth } = SIZES[size];
  const product = useProductContext(props);

  const sizeStr = typeof imgSize === "number" ? imgSize + "px" : imgSize;

  const pdpUrl = useProductDetailsUrl(product);
  let imageCmp = <ProductImage size={size} />;
  if (clickable) {
    imageCmp = (
      <a href={pdpUrl} className={props.className} {...linkProps}>
        {imageCmp}
      </a>
    );
  }

  return (
    <Root size={sizeStr} gap={gapSize + "px"} logoWidth={logoWidth + "px"}>
      {imageCmp}
      {product.brandLogoUrl && <BrandLogo src={product.brandLogoUrl} alt="" />}
      {product.manufacturerLogoUrl && <ManufacturerLogo src={product.manufacturerLogoUrl} alt="" />}
    </Root>
  );
};

interface PropsV2 extends BaseProductElementProps {
  size: number | string;
  gap?: number;
  logoWidth?: number;
  linkProps?: PdpLinkProps;
}

export const PrimaryImageWithLogosV2 = (props: PropsV2) => {
  const { size, gap = 16, logoWidth = 32 } = props;
  const product = useProductContext(props);

  const sizeStr = typeof size === "number" ? size + "px" : size;

  return (
    <Root size={sizeStr} gap={gap + "px"} logoWidth={logoWidth + "px"}>
      <ProductImage size={size} />
      {product.brandLogoUrl && <BrandLogo src={product.brandLogoUrl} alt="" />}
      {product.manufacturerLogoUrl && <ManufacturerLogo src={product.manufacturerLogoUrl} alt="" />}
    </Root>
  );
};

const Root = styled("div")<{ size: number | string; gap: string; logoWidth: string }>(({ size, gap, logoWidth }) => ({
  "--img-size": size,
  "--gap-size": gap,
  "--logo-width": logoWidth,
  display: "grid",
  gridTemplateColumns: `var(--img-size) var(--logo-width)`,
  gridTemplateRows: "repeat(2, calc(var(--img-size) / 2))",
  columnGap: "var(--gap-size)",
}));

const ProductImage = styled(PrimaryImageV2)({
  gridRow: "1 / 3",
  gridColumn: "1 / 2",
  width: "var(--img-size)",
  height: "var(--img-size)",
  objectFit: "contain",
});

const BrandLogo = styled("img")({
  gridColumn: "2 / 3",
  gridRow: "1 / 2",
  alignSelf: "flex-start",
  maxHeight: "unset",
});

const ManufacturerLogo = styled("img")({
  gridColumn: "2 / 3",
  gridRow: "2 / 3",
  alignSelf: "flex-end",
  maxHeight: "unset",
});
